/**
 * JobDetailPage - Job details with tabbed interface
 * Refactored to use useJobHandlers hook for all API operations
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import apiClient from "../services/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Image, ClipboardList, FileText, Calculator, StickyNote, Package, FolderOpen, ShieldCheck } from "lucide-react";
import { Tabs, TabsContent } from "../components/ui/tabs";
import { UnderlineTabsList as TabsList, UnderlineTabsTrigger as TabsTrigger } from "../components/ui/tabs-variants";
import {
  JobHeader,
  FinancialSummary,
  EstimatingTabWithSubTabs,
  ChangeOrdersTab,
  BillingsTab,
  PhotosTab,
  NotesTab,
  ContractReceivedDialog,
} from "../components/job";
import { useJobHandlers } from "../hooks/useJobHandlers";
import InvoicesPOsTab from "../components/job/InvoicesPOsTab";
import DocumentsTab from "../components/job/DocumentsTab";
import CertifiedPayrollTab from "../components/job/CertifiedPayrollTab";
import MissingAddressPrompt from "../components/job/MissingAddressPrompt";
import JobContactsCard from "../components/job/JobContactsCard";
const JobDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAuthHeaders, user } = useAuth();
  
  // Core state
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  // Reference data
  const [employees, setEmployees] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [generalContractors, setGeneralContractors] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [materialCategories, setMaterialCategories] = useState([]);
  const [equipmentCategories, setEquipmentCategories] = useState([]);
  const [equipmentUnits, setEquipmentUnits] = useState([]);
  const [employeeRoles, setEmployeeRoles] = useState([]);
  const [miscItemsFromSettings, setMiscItemsFromSettings] = useState([]);
  const [miscUnits, setMiscUnits] = useState([]);
  const [defaultBurdenPercent, setDefaultBurdenPercent] = useState(28);
  const [defaultSalesTaxPercent, setDefaultSalesTaxPercent] = useState(8.25);
  const [allUsers, setAllUsers] = useState([]);
  
  // Dialog state
  const [contractReceivedDialogOpen, setContractReceivedDialogOpen] = useState(false);

  // Permissions
  const canManageJobs = user?.role === "admin" || user?.role === "project_manager" || user?.role === "office_manager";
  const canViewEstimating = user?.role === "admin" || user?.role === "project_manager" || user?.role === "office_manager";
  const canEditEstimating = user?.role === "admin" || user?.role === "project_manager";

  // Fetch job and reference data
  const fetchData = useCallback(async () => {
    try {
      const [jobRes, employeesRes, materialsRes, optionsRes, usersRes] = await Promise.all([
        apiClient.get(`/jobs/${id}`),
        apiClient.get(`/employees`),
        apiClient.get(`/materials`),
        apiClient.get(`/settings/options`),
        apiClient.get(`/users`).catch(() => ({ data: [] })),
      ]);
      setJob(jobRes.data);
      setEmployees(employeesRes.data);
      setMaterials(materialsRes.data);
      setSuppliers(optionsRes.data.suppliers || []);
      setGeneralContractors(optionsRes.data.general_contractors || []);
      setEquipment(optionsRes.data.equipment || []);
      setMaterialCategories(optionsRes.data.material_categories || []);
      setEquipmentCategories(optionsRes.data.equipment_categories || []);
      setEquipmentUnits(optionsRes.data.equipment_units || []);
      setEmployeeRoles(optionsRes.data.employee_roles || []);
      setMiscItemsFromSettings(optionsRes.data.misc_items || []);
      setMiscUnits(optionsRes.data.misc_units || []);
      setDefaultBurdenPercent(optionsRes.data.default_burden_percent || 28);
      setDefaultSalesTaxPercent(optionsRes.data.default_sales_tax_percent || 8.25);
      setAllUsers(usersRes.data || []);
    } catch (error) {
      toast.error("Failed to load job details");
      navigate("/jobs");
    } finally {
      setLoading(false);
    }
  }, [id, getAuthHeaders, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get all handlers from custom hook
  const handlers = useJobHandlers({ jobId: id, getAuthHeaders, setJob, fetchData, navigate });

  // Dialog helpers
  const handleContractReceivedSubmit = async (data) => {
    // Enrich with GC address data from settings
    if (data.contractedGcId && generalContractors.length > 0) {
      const gc = generalContractors.find(g => g.id === data.contractedGcId);
      if (gc) {
        data.gcAddress = {
          street: gc.street || "",
          apt: gc.apt || "",
          city: gc.city || "",
          state: gc.state || "",
          zip_code: gc.zip_code || "",
        };
      }
    }
    const success = await handlers.handleContractReceived(job, data);
    if (success) setContractReceivedDialogOpen(false);
  };

  // Computed values
  const bidSheets = job?.bid_sheets || [];
  
  // Use backend-calculated totals - no need to recalculate here
  // job.estimate_total already includes approved change orders
  const totalChangeOrders = (job?.change_orders || [])
    .filter(co => co.date_approved)
    .reduce((sum, co) => sum + (co.amount || 0), 0);

  // Loading state
  if (loading || !job) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading job...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <JobHeader
        job={job}
        canManageJobs={canManageJobs}
        totalChangeOrders={totalChangeOrders}
        effectiveContract={job.estimate_total || job.contract_amount || 0}
        onBack={() => navigate("/jobs")}
        onDelete={handlers.handleDelete}
        onStatusChange={handlers.handleStatusChange}
        onContractReceived={() => setContractReceivedDialogOpen(true)}
        onUpdateAddendums={handlers.handleUpdateAddendums}
      />

      <MissingAddressPrompt job={job} canManageJobs={canManageJobs} onSaved={setJob} />

      <JobContactsCard job={job} canManageJobs={canManageJobs} />

      <FinancialSummary
        job={job}
        invoicesOutstanding={job.supplier_invoices?.filter(i => !i.paid).reduce((sum, i) => sum + (i.amount || 0), 0) || 0}
      />

      <Tabs defaultValue={searchParams.get('tab') || "estimating"} className="space-y-6">
        <TabsList data-testid="job-main-tabs">
            <TabsTrigger value="estimating" data-testid="estimating-tab">
              <Calculator className="w-4 h-4 hidden sm:inline" />Estimating
            </TabsTrigger>
          <TabsTrigger value="change-orders" data-testid="change-orders-tab">
            <ClipboardList className="w-4 h-4 hidden sm:inline" />Change Orders
          </TabsTrigger>
          <TabsTrigger value="invoices-pos" data-testid="invoices-pos-tab">
            <Package className="w-4 h-4 hidden sm:inline" />Invoices/POs
          </TabsTrigger>
          <TabsTrigger value="billings" data-testid="billings-tab">
            <FileText className="w-4 h-4 hidden sm:inline" />Billings
          </TabsTrigger>
          <TabsTrigger value="notes" data-testid="notes-tab">
            <StickyNote className="w-4 h-4 hidden sm:inline" />Notes/Follow-Up
          </TabsTrigger>
          <TabsTrigger value="photos" data-testid="photos-tab">
            <Image className="w-4 h-4 hidden sm:inline" />Photos ({job.photos?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="documents-tab">
            <FolderOpen className="w-4 h-4 hidden sm:inline" />Documents
          </TabsTrigger>
          <TabsTrigger value="certified-payroll" data-testid="certified-payroll-tab">
            <ShieldCheck className="w-4 h-4 hidden sm:inline" />Certified Payroll
          </TabsTrigger>
        </TabsList>

          <TabsContent value="estimating">
            <EstimatingTabWithSubTabs
              job={job}
              materials={materials}
              equipment={equipment}
              suppliers={suppliers}
              materialCategories={materialCategories}
              equipmentCategories={equipmentCategories}
              equipmentUnits={equipmentUnits}
              employeeRoles={employeeRoles}
              miscItemsFromSettings={miscItemsFromSettings}
              miscUnits={miscUnits}
              defaultBurdenPercent={defaultBurdenPercent}
              defaultSalesTaxPercent={defaultSalesTaxPercent}
              canManageJobs={canManageJobs}
              canEditEstimating={canEditEstimating}
              userRole={user?.role}
              bidSheets={bidSheets}
              onSaveBidSheet={handlers.handleSaveBidSheets}
              onApproveCO={handlers.handleApproveCOBidSheet}
              onRevokeCOApproval={handlers.handleRevokeCOApproval}
              onSaveLaborEstimates={handlers.handleSaveLaborEstimates}
              onSaveMaterialEstimates={handlers.handleSaveMaterialEstimates}
              onSaveEquipmentEstimates={(estimates) => handlers.handleSaveEquipmentEstimates(estimates, job)}
              onPopulateFromBidSheet={() => handlers.handlePopulateFromBidSheet(job)}
              onAddInvoice={handlers.handleAddInvoice}
              onEditInvoice={handlers.handleEditInvoice}
              onDeleteInvoice={handlers.handleDeleteInvoice}
              onToggleInvoicePaid={handlers.handleToggleInvoicePaid}
              onRefreshJob={fetchData}
              getAuthHeaders={getAuthHeaders}
            />
          </TabsContent>

        <TabsContent value="change-orders">
          <ChangeOrdersTab
            job={job}
            canManageJobs={canManageJobs}
            totalChangeOrders={totalChangeOrders}
            effectiveContract={job.estimate_total || job.contract_amount || 0}
            onAddChangeOrder={handlers.handleAddChangeOrder}
            onEditChangeOrder={handlers.handleEditChangeOrder}
            onDeleteChangeOrder={handlers.handleDeleteChangeOrder}
          />
        </TabsContent>

        <TabsContent value="invoices-pos">
          <InvoicesPOsTab
            job={job}
            canManageJobs={canManageJobs}
            suppliers={suppliers}
            generalContractors={generalContractors}
            onAddInvoice={handlers.handleAddInvoice}
            onEditInvoice={handlers.handleEditInvoice}
            onDeleteInvoice={handlers.handleDeleteInvoice}
            onToggleInvoicePaid={handlers.handleToggleInvoicePaid}
            onRefreshJob={fetchData}
            getAuthHeaders={getAuthHeaders}
          />
        </TabsContent>

        <TabsContent value="billings">
          <BillingsTab
            job={job}
            canManageJobs={canManageJobs}
            generalContractors={generalContractors}
            onAddBilling={handlers.handleAddBilling}
            onEditBilling={handlers.handleEditBilling}
            onDeleteBilling={handlers.handleDeleteBilling}
            onToggleEdited={handlers.handleToggleBillingEdited}
            onRefreshJob={fetchData}
          />
        </TabsContent>

        <TabsContent value="notes">
          <NotesTab
            job={job}
            users={allUsers}
            currentUserId={user?.id}
            canManageJobs={canManageJobs}
            onAddNote={handlers.handleAddNote}
            onEditNote={handlers.handleEditNote}
            onDeleteNote={handlers.handleDeleteNote}
            onUpdateJob={async (updates) => {
              if (updates.bidding_gcs) {
                await handlers.handleUpdateBiddingGCs(updates.bidding_gcs);
              }
            }}
          />
        </TabsContent>

        <TabsContent value="photos">
          <PhotosTab
            job={job}
            jobId={id}
            canManageJobs={canManageJobs}
            uploading={uploading}
            onPhotoUpload={(e) => handlers.handlePhotoUpload(e, setUploading)}
            onDeletePhoto={handlers.handleDeletePhoto}
            onCaptionUpdate={handlers.handleCaptionUpdate}
          />
        </TabsContent>

        <TabsContent value="documents">
          <DocumentsTab jobId={id} canManageJobs={canManageJobs} />
        </TabsContent>

        <TabsContent value="certified-payroll">
          <CertifiedPayrollTab job={job} canManageJobs={canManageJobs} onJobUpdated={setJob} />
        </TabsContent>
      </Tabs>

      <ContractReceivedDialog
        open={contractReceivedDialogOpen}
        onOpenChange={setContractReceivedDialogOpen}
        job={job}
        onContractReceived={handleContractReceivedSubmit}
        generalContractors={generalContractors}
      />
    </div>
  );
};

export default JobDetailPage;
